import { Autocomplete, Box, debounce, Skeleton, Stack, Typography } from '@mui/material';
import {
  ConversationChannelType,
  ConversationParticipantEntity,
  ConversationParticipationMode,
} from '@provision/conversation-types';
import { ConversationParticipation } from '@provision/entity-types';
import { useCallback, useMemo, useState } from 'react';
import { groupBy } from 'remeda';
import { SearchBar } from 'src/components/SearchBar/search-bar';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import {
  Conversation,
  ConversationParticipantItem,
  PreparedConversationParticipantOption,
  useCanUpdateConversation,
  useConversationParticipantOptions,
  useConversationParticipants,
} from 'src/graphql';
import { useConversationParticipation } from 'src/graphql/entities/conversation-participation/conversation-participation';
import { useConversationParticipationRecords } from 'src/graphql/entities/conversation-participation/hooks/use-conversation-participation-records';
import { usePlatformUserIds } from 'src/graphql/entities/platform-user';
import { ConditionInput } from 'src/graphql/types';
import { RequesterListItem } from 'src/pages/Conversations/SidePanel/ConversationDetails/requester-list-item';
import { ConversationParticipantInfo } from './conversation-participant-info';

const SKELETON_ELEMENTS = 3;

const ConversationParticipantSkeleton = () => (
  <Stack direction="row" gap={2} alignItems="center">
    <Skeleton
      variant="circular"
      width={25}
      height={25}
      sx={{
        aspectRatio: '1',
      }}
    />
    <Stack
      direction="row"
      width="calc(100% - 15px)"
      justifyContent="space-between"
      spacing={2}
    >
      <Skeleton width="80%" />
    </Stack>
  </Stack>
);

const BASE_PARTICIPANTS_FILTERS: ConditionInput[] = [
  {
    attribute: 'participantEntity',
    operator: '!=',
    value: ConversationParticipantEntity.Bot,
  },
];

export const ConversationParticipants = ({
  conversation,
  onSuccessSubmit,
}: {
  conversation: Conversation;
  onSuccessSubmit?: () => void;
}) => {
  const [searchParticipantsString, setSearchParticipantsString] = useState('');
  const { conversationParticipations: participations } = useConversationParticipationRecords({
    conversationId: conversation.id,
    includeDeleted: true,
  });

  const shouldDisableForm = !useCanUpdateConversation({
    conversationStatusId: conversation.conversationStatusId,
    isDeleted: conversation.isDeleted,
  });

  const participantsQueryParams = useMemo(() => {
    return {
      participantIds: participations.map(
        (participation) => participation.channel.conversationParticipantId,
      ),
      filter: BASE_PARTICIPANTS_FILTERS,
    };
  }, [participations]);

  const { conversationParticipants, loading: isLoading } = useConversationParticipants(
    participantsQueryParams,
    {
      skip: !participations?.length,
    },
  );

  const { data: activeUserRecordsIds } = usePlatformUserIds({
    partnerId: conversation?.partner?.id,
    filters: [
      {
        attribute: 'isActive',
        operator: '=',
        value: 'true',
      },
    ],
  });

  const { unknown: unknownConversationParticipants, known: knownConversationParticipants } =
    useMemo(() => {
      const activeParticipationChannelIds = new Set(
        participations
          .filter((participation) => participation.isActive)
          .map((participation) => participation.channel.id),
      );

      const preparedParticipants = (conversationParticipants ?? [])
        .filter((participant) =>
          participant.conversationChannels.some((channel) =>
            activeParticipationChannelIds.has(channel.id),
          ),
        )
        .map((participant) => {
          return {
            ...participant,
            isActive:
              (participant.participantEntity as ConversationParticipantEntity) !==
                ConversationParticipantEntity.PlatformUser ||
              activeUserRecordsIds.includes(participant.participantIdentity),
          };
        })
        // Remove duplicated elements.
        // Operations like Merge, could lead to duplicated elements
        .filter(
          (participant, index, arr) =>
            arr.findIndex((p) => p.participantIdentity === participant.participantIdentity) ===
            index,
        );

      return groupBy(preparedParticipants, (participant) =>
        (participant.participantEntity?.toLowerCase() as ConversationParticipantEntity) ===
        ConversationParticipantEntity.Unknown
          ? 'unknown'
          : 'known',
      );
    }, [conversationParticipants, activeUserRecordsIds, participations]);

  const { getConversationParticipantOptions, conversationParticipantOptions } =
    useConversationParticipantOptions();

  const triggerParticipantsSearch = useMemo(
    () =>
      debounce((value: string) => {
        getConversationParticipantOptions({
          partnerId: conversation?.partnerId,
          searchText: value,
          skipClients: true,
        });
      }, SEARCH_DEBOUNCE_DELAY),
    [getConversationParticipantOptions, conversation?.partnerId],
  );

  const preparedOptions = useMemo(
    () =>
      conversationParticipantOptions.filter(
        (option) => option.participantEntity === ConversationParticipantEntity.PlatformUser,
      ),
    [conversationParticipantOptions],
  );

  const {
    addParticipationWithChannel,
    deactivateParticipation,
    createParticipationWithRelatedData,
  } = useConversationParticipation();

  const toggleFollow = useCallback(
    (data: {
      emailParticipation?: ConversationParticipation | null;
      participant: ConversationParticipantItem;
    }) => {
      const { emailParticipation, participant } = data;

      if (emailParticipation?.isActive) {
        deactivateParticipation(emailParticipation.channel, conversation);
      } else {
        addParticipationWithChannel({
          participantIdentity: participant.participantIdentity,
          conversation,
          email: participant.platformUser.email,
          participantEntity: ConversationParticipantEntity.PlatformUser,
        });
      }
    },
    [conversation, deactivateParticipation, addParticipationWithChannel],
  );

  const handlePersonSelect = useCallback(
    (data: PreparedConversationParticipantOption | null) => {
      if (!data?.id) {
        return;
      }

      let participationMode = ConversationParticipationMode.Assist;

      const channels = [
        { channelType: ConversationChannelType.Chat, externalIdentifier: data.id },
      ];

      if (data.participantEntity === ConversationParticipantEntity.Client) {
        participationMode = ConversationParticipationMode.Engage;

        if (data.email) {
          channels.push({
            channelType: ConversationChannelType.Email,
            externalIdentifier: data.email,
          });
        }
      }

      return createParticipationWithRelatedData({
        participantEntity: data.participantEntity,
        participationMode,
        entityIdentifier: data.id,
        channels,
        conversationId: conversation.id,
        partnerId: conversation.partnerId,
        partnerEmail:
          conversation.partner.brandGroups?.[0]?.supportEmail ??
          conversation.partner.supportEmail,
        conversation,
      });
    },
    [conversation, createParticipationWithRelatedData],
  );

  return (
    <Stack>
      <Autocomplete<PreparedConversationParticipantOption, false, true, true>
        freeSolo
        disableClearable
        options={preparedOptions}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : (option.email ?? '')
        }
        onInputChange={(_: unknown, value: string, reason: string) => {
          if (reason === 'input') {
            setSearchParticipantsString(value);
            triggerParticipantsSearch(value);
          }
        }}
        inputValue={searchParticipantsString}
        onChange={(_: unknown, v: PreparedConversationParticipantOption | string) => {
          setSearchParticipantsString('');

          if (typeof v !== 'string') {
            void handlePersonSelect(v);
          }
        }}
        filterOptions={(x) => x}
        slotProps={{ popper: { sx: { minWidth: 350, zIndex: 1300 } } }}
        sx={{ flex: '1 1 auto' }}
        renderInput={(params) => (
          <SearchBar
            params={params}
            title="Participants"
            placeholder="Search to add an agent"
          />
        )}
        renderOption={(props, option) => (
          <Box key={option.id}>
            <RequesterListItem
              props={props}
              id={option.id}
              key={option.id}
              firstLine={option.label}
              secondLine={option.email}
              externalPartnerId={
                option.entityPartnerId !== conversation.partner?.id
                  ? option.entityPartnerId
                  : undefined
              }
            />
          </Box>
        )}
      />

      <Stack gap={1}>
        {isLoading ? (
          Array.from({ length: SKELETON_ELEMENTS }, (_, i) => (
            <ConversationParticipantSkeleton key={i} />
          ))
        ) : (
          <>
            {unknownConversationParticipants?.map((participant) => (
              <ConversationParticipantInfo
                key={participant.id}
                participant={participant}
                participations={participations}
                conversation={conversation}
                text={
                  participant?.participantIdentity ||
                  participant.conversationChannels.find(
                    (conversationChannel) => !!conversationChannel.externalIdentifier,
                  )?.externalIdentifier
                }
                extraInfo={participant.participantEntity}
                isUserActive={participant.isActive}
                onSuccessSubmit={onSuccessSubmit}
                readonly={shouldDisableForm}
                {...(!shouldDisableForm ? { icon: 'add_circle' } : {})}
              />
            ))}
            {knownConversationParticipants?.map((participant) => {
              const isAgent =
                (participant.participantEntity.toLowerCase() as ConversationParticipantEntity) ===
                ConversationParticipantEntity.PlatformUser;

              let icon = 'person';

              switch (participant.participantEntity) {
                case ConversationParticipantEntity.PlatformUser.toString():
                  icon = 'support_agent';
                  break;
                case ConversationParticipantEntity.Rsm.toString():
                  icon = 'radar';
                  break;
              }

              return (
                <ConversationParticipantInfo
                  key={participant.id}
                  participations={participations}
                  conversation={conversation}
                  participant={participant}
                  icon={icon}
                  text={
                    participant.platformUser?.fullName ||
                    participant.client?.fullName ||
                    participant.rsmEmail?.name
                  }
                  isUserActive={participant.isActive}
                  onSuccessSubmit={onSuccessSubmit}
                  toggleFollow={isAgent ? toggleFollow : undefined}
                  readonly={shouldDisableForm}
                />
              );
            })}
          </>
        )}

        {!isLoading && !conversationParticipants?.length && (
          <Stack padding="0.5rem">
            <Typography alignSelf="center" variant="body2">
              No results
            </Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
